import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { generateTestId, getMediaPath } from 'app/functions';

import theme from 'theme';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: '40px',
  paddingTop: '40px',
  paddingBottom: '48px',
  paddingLeft: '24px',
  paddingRight: '24px',
  position: 'relative',
  [theme.breakpoints.only('md')]: {
    paddingTop: '40px',
  },
  [theme.breakpoints.up('xl')]: {
    marginTop: '30px',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#1D094C',
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: 'normal',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '32px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '40px',
  },
}));

const CategoryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '30px',
  gap: '25px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('lg')]: {
    justifyContent: 'space-around',
    flexWrap: 'nowrap',
    gap: '20px',
  },
  [theme.breakpoints.up('xl')]: {
    justifyContent: 'space-between',
    gap: '40px',
  },
}));

const CategoryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  padding: '4px 0',
  width: 'fit-content',
  height: 'auto',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    padding: '6px 4px 6px 6px',
    borderRadius: '12px',
    boxShadow: theme.palette.brands.shadow.active,
  },
  '&.active': {
    transform: 'translateY(-2px)',
    padding: '6px 4px 6px 6px',
    borderRadius: '12px',
    boxShadow: theme.palette.brands.shadow.active,
  },
  [theme.breakpoints.up('sm')]: {
    width: 'fit-content',
    height: 'auto',
    padding: '8px 4px',
    '&.active': {
      padding: '8px 4px',
    },
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '24px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '50.23px',
  height: '50.23px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'transparent',
  '& img': {
    width: '30px',
    height: '30px',
  },
  [theme.breakpoints.up('sm')]: {
    width: '55.23px',
    height: '55.23px',
    '& img': {
      width: '35px',
      height: '35px',
    },
  },
  [theme.breakpoints.up('md')]: {
    width: '60px',
    height: '60px',
    '& img': {
      width: '40px',
      height: '40px',
    },
  },
  [theme.breakpoints.up('lg')]: {
    width: '70px',
    height: '70px',
    '& img': {
      width: '50px',
      height: '50px',
    },
  },
  [theme.breakpoints.up('xl')]: {
    width: '90px',
    height: '90px',
    '& img': {
      width: '50px',
      height: '50px',
    },
  },
}));

const CategoryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.brands.text.inactive,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '1.08154296875em',
  textAlign: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
  '&.active': {
    color: theme.palette.brands.text.active,
  },
  '&:hover': {
    color: theme.palette.brands.text.active,
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px',
    
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '22px',
  },
}));

const BrandsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '70px',
  gap: '24px',
  position: 'relative',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',  
  [theme.breakpoints.up('sm')]: {
    height: '80px',
    gap: '30px',
  },
  [theme.breakpoints.up('md')]: {
    height: '90px',
  },
  [theme.breakpoints.up('lg')]: {
    height: '100px',
  },
  [theme.breakpoints.up('xl')]: {
    height: '110px',
  },
}));

const BrandImage = styled('img')({
  maxWidth: '60px',
  height: '60px',
  objectFit: 'contain',
  transition: 'all 0.3s ease',
  '&:hover, &:active': {
    transform: 'scale(1.1)',
    filter: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: '70px',
    height: '70px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '80px',
    height: '80px',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '90px',
    height: '90px',
  },
  [theme.breakpoints.up('xl')]: {
    maxWidth: '95px',
    height: '95px'
  },
});

const GradientOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '20px',
  pointerEvents: 'none',
  zIndex: 1,
  '&.left': {
    left: 0,
    background: `linear-gradient(-90deg, ${theme.palette.brands.background.default} 70.64%, rgba(255, 255, 255, 0) 100%)`,
  },
  '&.right': {
    right: 0,
    background: `linear-gradient(90deg, ${theme.palette.brands.background.default} 70.64%, rgba(255, 255, 255, 0) 100%)`,
  },
  [theme.breakpoints.up('sm')]: {
    width: '150px',
  
  },
}));

const BrandsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  padding: '0 8px',
  minWidth: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  overflowX: 'visible',
  flexWrap: 'nowrap',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
    gap: '28px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '32px',
  },
  [theme.breakpoints.up('lg')]: {
    gap: '60px',
  },
  [theme.breakpoints.up('xl')]: {
    gap: '80px',
  },
}));

const TopBrandsSection = ({ data, testID, }) => {

  if(!data?.categories?.length) return null;

  const [activeCategory, setActiveCategory] = useState(data.categories[0].title);
  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const activeCategoryBrands = data.categories.find((category) => category.title === activeCategory)?.brands || [];

  return (
    <Container testID={generateTestId(testID, 'root')} >
      <Title testID={generateTestId(testID, 'title')}><Typography variant="sectionTitle">{data.title}</Typography></Title>
      <CategoryRow testID={generateTestId(testID, 'categoryRow')}>
        {data.categories.map((category, index) => (
          <CategoryItem
            key={category.title}
            className={activeCategory === category.title ? 'active' : ''}
            onClick={() => handleCategoryClick(category.title)}
            testID={generateTestId(testID, `categoryItem-${category.title}`)}
          >
              <IconContainer testID={generateTestId(testID, `iconContainer-${category.title}`)}>
              <img src={getMediaPath(category.icon)} alt={category.title} testID={generateTestId(testID, `icon_${category.index}`)} />
            </IconContainer>
            <CategoryText 
              className={activeCategory === category.index ? 'active' : ''}
              testID={generateTestId(testID, `categoryText-${category.index}`)}
            >
              {category.title}
            </CategoryText>
          </CategoryItem>
        ))}
      </CategoryRow>
      <div style={{ position: 'relative', width: '100%' }}>
        <GradientOverlay className="left" />
        <BrandsRow testID={generateTestId(testID, 'brandsRow')}>
          <BrandsContainer 
            testID={generateTestId(testID, 'brandsContainer')}
          >
            {activeCategoryBrands.map((brand, index) => (
              <BrandImage
                key={brand.imagePath + `-${index}`}
                src={getMediaPath(brand.imagePath)}
                alt={`Brand ${index + 1}`}
                testID={generateTestId(testID, `brandImage-${index}`)}
              />
            ))}
          </BrandsContainer>
        </BrandsRow>
        <GradientOverlay className="right" />
      </div>
    </Container>
  );
};

export default TopBrandsSection; 